import React from 'react';

import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import SecondaryNav from './SecondaryNav';
import DownloadProgressBar from 'Data/DownloadData/DownloadProgressBar';
import { Breadcrumbs } from './Breadcrumbs';
import qs from 'qs';
import { getQueryParams } from 'utils/query-string';
import Explore from 'Explore';
import ChartOptions from './ChartOptions';
import WaterfallChart from './WaterfallChart';
import RegionalSection from './RegionalSection';
import CommonVendors from './CommonVendors';
import ShareOfTotalSpendSection from './ShareOfTotalSpendSection';
import CategorySpendTreeChartSection from './CategorySpendTreeChartSection';
import {
	BREAKDOWN_BY,
	CHART_TYPES,
	SPEND_TYPE_LABELS,
	NAV_CONFIG,
} from './utils';
import { InvestigateContext } from './context';
import { generateHeroChartDataFile } from 'utils/apiRequests';
import {
	PersonalizeOrder,
	isConfigItemVisible,
	getConfigIndex,
} from 'components/PersonalizeOrder';
import {
	ScrollContainer,
	Wrapper,
	FixedHeader,
	SecondaryContent,
	OrderedItemWrapper,
	LoadingWrapper,
} from './Investigate.styles';
import { downloadDeck } from 'utils/apiRequests';
import { Fragmentation } from './Fragmentation/Fragmentation';

const PERSONALIZE_CONFIG = [
	{ id: 'RegionalSection', name: 'Regional Spend' },
	{ id: 'CommonVendors', name: 'Top 10 Vendors' },
	{ id: 'ShareOfTotalSpendSection', name: 'Vendor Fragmentation' },
	{ id: 'CategorySpendTreeDirect', name: 'Category Spend Tree (Direct)' },
	{ id: 'CategorySpendTreeIndirect', name: 'Category Spend Tree (Indirect)' },
];

export class Investigate extends React.Component {
	state = {
		showFixedHeader: false,
		chartType: CHART_TYPES.WATERFALL,
		triggerContentRefresh: false,
		hasExportError: false,
		isNotificationShown: false,
		isDeckDownloading: false,
		selectedIndustries: [],
		isDownloadDeckModalOpen: false,
	};

	downloadDeckRef = React.createRef();

	handleDownloadDeck = async (e, type) => {
		e.preventDefault();
		const { dateTo, dateFrom } = this.props.apiParams;
		const param = {
			dateTo,
			dateFrom,
			scaleSymbol: 'm',
			industries: type == 'direct' ? [] : this.state.selectedIndustries,
			ApTaxonomy: type == 'direct' ? 0 : 1,
		};
		try {
			await downloadDeck(param);
			this.setState({
				isNotificationShown: true,
				isDownloadDeckModalOpen: false,
			});
		} catch (e) {
			this.setState({
				isNotificationShown: true,
				exportToXlsError: true,
				isDownloadDeckModalOpen: false,
			});
		}

		setTimeout(() => {
			this.closeNotification();
		}, 5000);
	};

	handleIndustries = industries => {
		this.setState({ selectedIndustries: industries });
	};

	handleIndustryModal = val => {
		this.setState({ isDownloadDeckModalOpen: val });
	};

	handleBreadcrumbsChange = (_, index) => {
		const { setQueryStringStateAndPush, queryParams } = this.props;
		const {
			hierarchy,
			splitSpendByDimension: prevSplitSpendByDimension,
			tailSpendLevel,
		} = queryParams;
		let newTailSpendValue = tailSpendLevel;
		let splitSpendByDimension = prevSplitSpendByDimension;

		// Root level
		if (index === 0) {
			if (splitSpendByDimension === BREAKDOWN_BY.TAIL_SEGMENT) {
				splitSpendByDimension = BREAKDOWN_BY.NONE;
			}
			setQueryStringStateAndPush({
				indirectTaxonomy: '1',
				hierarchy: [],
				spendType: undefined,
				tailSpendLevel: undefined,
				selectedItem: undefined,
				splitSpendByDimension,
			});

			return;
		}

		if (tailSpendLevel + 2 > index) {
			newTailSpendValue = undefined;
		}
		if (
			!newTailSpendValue &&
			splitSpendByDimension === BREAKDOWN_BY.TAIL_SEGMENT
		) {
			splitSpendByDimension = BREAKDOWN_BY.NONE;
		}

		setQueryStringStateAndPush({
			hierarchy: hierarchy.slice(0, index - 1),
			tailSpendLevel: newTailSpendValue,
			selectedItem: undefined,
			splitSpendByDimension,
		});
	};

	handleChartTypeChange = option => {
		this.setState({ chartType: option.value });
	};

	renderBreadcrumbs = () => {
		const { queryParams } = this.props;
		const {
			hierarchy,
			showNonSourceable,
			includeTailSpend,
			spendType,
			tailSpendLevel,
		} = queryParams;
		const rootLabel = !includeTailSpend
			? `All ${!showNonSourceable ? 'sourceable' : 'non-sourceable'} spend`
			: `${!showNonSourceable ? 'Sourceable' : 'Non-sourceable'} head spend`;
		const options = [
			{ label: rootLabel, value: '' },
			...(spendType != null
				? [{ label: SPEND_TYPE_LABELS[spendType], value: String(spendType) }]
				: []),
			...hierarchy.map(item => ({
				label: item,
				value: item,
			})),
		];

		if (Number.isInteger(Number(tailSpendLevel))) {
			options.splice(Number(tailSpendLevel) + 2, 0, {
				label: 'Tail spend',
				value: 'Tail spend',
			});
		}

		return (
			<Breadcrumbs options={options} onClick={this.handleBreadcrumbsChange} />
		);
	};

	handleScroll = e => {
		const { showFixedHeader } = this.state;
		const { scrollTop } = e.currentTarget;
		const newState = scrollTop > 79;

		if (newState !== showFixedHeader) {
			this.setState({ showFixedHeader: newState });
		}
	};

	handleHighchartsExportToPng = () => {
		if (this.chartRef) {
			const chartName = 'Waterfall';
			const chart = this.chartRef.current.chart;

			chart.exportChartLocal(
				{
					filename: chartName,
				},
				{
					chart: {
						width: 1240,
						height: 500,
					},
				}
			);
		}
	};

	handleExportToXls = async () => {
		try {
			await generateHeroChartDataFile(this.props.apiParams);

			this.setState({ showDownloadNotification: true });
		} catch (e) {
			this.setState({ showDownloadNotification: true, exportToXlsError: true });
		}

		setTimeout(() => {
			this.closeNotification();
		}, 5000);
	};

	closeNotification = () => {
		this.setState({ isNotificationShown: false, hasExportError: false });
	};

	downloadFailed = () => {
		this.setState({ hasExportError: true });
	};

	clearDownloadError = () => {
		this.setState({ hasExportError: false });
	};

	showDownloadNotification = () => {
		this.setState({ isNotificationShown: true });

		setTimeout(() => {
			this.closeNotification();
		}, 5000);
	};

	refreshContent = () => {
		this.setState({ triggerContentRefresh: true }, () => {
			this.setState({ triggerContentRefresh: false });
		});
	};

	showExplorePage = (overrides, transactionsTitle) => {
		const { history, location } = this.props;
		const currentQueryParams = getQueryParams(location);
		const queryStringState = {
			...currentQueryParams,
			...overrides,
		};

		const prevParams = {
			// Need to cleanup because if some props are empty they will not get updated in query string state
			...Object.keys(overrides).reduce((acc, param) => {
				return { ...acc, [param]: null };
			}, {}),
			...currentQueryParams,
		};
		const prevSearch = qs.stringify(prevParams, {
			strictNullHandling: true,
			arrayFormat: 'repeat',
		});
		const search = qs.stringify(queryStringState, {
			strictNullHandling: true,
			arrayFormat: 'repeat',
		});
		const locationUrl = `${NAV_CONFIG[2].path}?${search}`;

		history.push(locationUrl, {
			prevPageUrl: `${location.pathname}${prevSearch ? `?${prevSearch}` : ''}`,
			transactionsTitle,
		});
	};

	handleNavigationBackButtonClick = () => {
		const { location, history } = this.props;
		const { prevPageUrl } = location.state || {};

		const locationStateCleanup = {
			prevPageUrl: null,
			selectedDimensionKey: null,
			selectedDimensionName: null,
		};

		history.push(prevPageUrl, locationStateCleanup);
	};

	handleSetTopChartRef = chartRef => {
		this.chartRef = chartRef;
	};

	render() {
		const {
			showFixedHeader,
			chartType,
			isDownloadDeckModalOpen,
			isNotificationShown,
			hasExportError,
			triggerContentRefresh,
		} = this.state;
		const { apiParams, queryParams, currencySymbol, location } = this.props;
		const { hierarchy, spendType } = queryParams;
		const { transactionsTitle } = location.state || {};
		const isExplorePage =
			location.pathname && location.pathname.includes('/explore');

		return (
			<InvestigateContext.Provider
				value={{ refreshContent: this.refreshContent }}
			>
				<Wrapper>
					<SecondaryNav
						transactionsTitle={transactionsTitle}
						onBackButtonClick={this.handleNavigationBackButtonClick}
						showExplorePage={this.showExplorePage}
					/>
					{isExplorePage ? (
						<Explore hideNav />
					) : (
						<ScrollContainer onScroll={this.handleScroll}>
							{showFixedHeader &&
								(hierarchy.length > 0 || spendType != null) && (
									<FixedHeader>{this.renderBreadcrumbs()}</FixedHeader>
								)}
							<ChartOptions
								handleDownloadDeck={this.handleDownloadDeck}
								handleIndustries={this.handleIndustries}
								handleIndustryModal={this.handleIndustryModal}
								isDeckDownloading={this.state.isDeckDownloading}
								handleHighchartsExportToPng={this.handleHighchartsExportToPng}
								handleExportToXls={this.handleExportToXls}
								breadcrumbs={this.renderBreadcrumbs()}
								chartType={chartType}
								isDownloadDeckModalOpen={isDownloadDeckModalOpen}
								onChartTypeChange={this.handleChartTypeChange}
								hierarchy={hierarchy}
								hideBreakdown={spendType == null}
							/>
							{!apiParams ? (
								<LoadingWrapper>
									<ApLoaderDefault loaderText={() => <ApLoaderText />} />
								</LoadingWrapper>
							) : (
								<React.Fragment>
									{!triggerContentRefresh && (
										<WaterfallChart
											chartType={chartType}
											hierarchy={hierarchy}
											apiParams={apiParams}
											showExplorePage={this.showExplorePage}
											setChartRef={this.handleSetTopChartRef}
										/>
									)}

									<SecondaryContent>
										<PersonalizeOrder config={PERSONALIZE_CONFIG}>
											{configItems => (
												<React.Fragment>
													{isConfigItemVisible(
														configItems,
														'RegionalSection'
													) && (
														<OrderedItemWrapper
															id="RegionalSection"
															order={getConfigIndex(
																configItems,
																'RegionalSection'
															)}
														>
															{!triggerContentRefresh && (
																<RegionalSection
																	cardTitle={PERSONALIZE_CONFIG[0].name}
																	currencySymbol={currencySymbol}
																	queryParams={apiParams}
																	onDownloadError={this.downloadFailed}
																	onDownloadSuccess={
																		this.showDownloadNotification
																	}
																	onDownloadStart={this.clearDownloadError}
																	showExplorePage={this.showExplorePage}
																/>
															)}
														</OrderedItemWrapper>
													)}
													{isConfigItemVisible(
														configItems,
														'CommonVendors'
													) && (
														<OrderedItemWrapper
															id="CommonVendors"
															order={getConfigIndex(
																configItems,
																'CommonVendors'
															)}
														>
															{!triggerContentRefresh && (
																<CommonVendors
																	cardTitle={PERSONALIZE_CONFIG[1].name}
																	currencySymbol={currencySymbol}
																	queryParams={apiParams}
																	spendType={spendType}
																	onDownloadError={this.downloadFailed}
																	onDownloadSuccess={
																		this.showDownloadNotification
																	}
																	onDownloadStart={this.clearDownloadError}
																	showExplorePage={this.showExplorePage}
																/>
															)}
														</OrderedItemWrapper>
													)}
													{isConfigItemVisible(
														configItems,
														'ShareOfTotalSpendSection'
													) && (
														<OrderedItemWrapper
															id="ShareOfTotalSpendSection"
															order={getConfigIndex(
																configItems,
																'ShareOfTotalSpendSection'
															)}
														>
															{!triggerContentRefresh && (
																<>
																	{spendType === 0 &&
																	queryParams.indirectTaxonomy === 2 &&
																	queryParams.directTaxonomy === 2 ? (
																		<Fragmentation
																			currencySymbol={currencySymbol}
																			queryParams={apiParams}
																			spendType={spendType}
																			onDownloadError={this.downloadFailed}
																			onDownloadSuccess={
																				this.showDownloadNotification
																			}
																			onDownloadStart={this.clearDownloadError}
																			showExplorePage={this.showExplorePage}
																		/>
																	) : (
																		<ShareOfTotalSpendSection
																			cardTitle={PERSONALIZE_CONFIG[2].name}
																			currencySymbol={currencySymbol}
																			queryParams={apiParams}
																			spendType={spendType}
																			onDownloadError={this.downloadFailed}
																			onDownloadSuccess={
																				this.showDownloadNotification
																			}
																			onDownloadStart={this.clearDownloadError}
																			showExplorePage={this.showExplorePage}
																			allSpend={true}
																		/>
																	)}
																</>
															)}
														</OrderedItemWrapper>
													)}
													{!triggerContentRefresh && (
														<CategorySpendTreeChartSection
															cardHeadings={PERSONALIZE_CONFIG}
															configItems={configItems}
															currencySymbol={currencySymbol}
															queryParams={apiParams}
															spendType={spendType}
															onDownloadError={this.downloadFailed}
															onDownloadSuccess={this.showDownloadNotification}
															onDownloadStart={this.clearDownloadError}
															showExplorePage={this.showExplorePage}
														/>
													)}
												</React.Fragment>
											)}
										</PersonalizeOrder>
									</SecondaryContent>
								</React.Fragment>
							)}
							{isNotificationShown && (
								<DownloadProgressBar
									error={hasExportError}
									successMessage="Generating file... You will receive a notification when the file has been generated"
									onClose={this.closeNotification}
								/>
							)}
						</ScrollContainer>
					)}
				</Wrapper>
			</InvestigateContext.Provider>
		);
	}
}

export default Investigate;
